import { TabbedLayoutTopBar } from '@/modules/application/components/TabbedLayoutTopBar';
import { TabbedLayout } from '@/modules/application/layouts/TabbedLayout';
import { LayoutActionsProvider } from '@/providers/layout-actions-provider/LayoutActionsProvider';
import { useState, useEffect, FunctionComponent } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { locationService } from '../api/locations/location.service';
import { LocationModel } from '../types/LocationModel';
import { BackButton } from '@/components/BackButton';
import NavigationTabs from '@/modules/application/components/NavigationTabs';
import TabbedLayoutTopBarHeader from '@/modules/application/components/TabbedLayoutTopBarHeader';
import { TabbedLayoutTopBarVerticalSeperator } from '@/modules/application/components/TabbedLayoutTopBarVerticalSeperator';
import { Skeleton } from '@mui/material';
import { TabbedPageLayout } from '@/modules/application/layouts/TabbedPageLayout';
import { TabbedPageLayoutBody } from '@/modules/application/components/TabbedPageLayoutBody';
import { TabMenuItem } from '@/modules/application/types/MenuItem';
import { useTranslation } from '@/lib';
import { useAuthUserContext } from '@/modules/auth';
import { isBetaTenant, isTransactionsOnlyTenant } from '@/config/tenant-features';

export const LocationDetailsLayout: FunctionComponent = () => {
  const { locationId } = useParams();
  const locationIdNumber = locationId ? Number.parseInt(locationId) : 0;
  const isCreate = locationId === 'create';
  const { t } = useTranslation();
  const { tenantId } = useAuthUserContext();
  const hasBetaAccess = isBetaTenant(tenantId);
  const isTransactionsOnly = isTransactionsOnlyTenant(tenantId);

  const [location, setLocation] = useState<LocationModel | undefined>(undefined);

  const backButtonPath = '/app/locations/overview';

  useEffect(() => {
    if (locationId) {
      locationService.getById(Number.parseInt(locationId)).then((location) => {
        if (location.isSuccess) {
          setLocation(location.payload);
        }
      });
    }
  }, [locationId]);

  const tabItems: TabMenuItem[] = [];

  if (isCreate) {
    tabItems.push({
      activeRouteIds: ['/app/locations/create'],
      label: t('details'),
      url: '/app/locations/create',
    });
  } else {
    tabItems.push({
      activeRouteIds: [`/app/locations/${locationIdNumber}/details`],
      label: t('details'),
      url: `/app/locations/${locationIdNumber}/details`,
    });
    // Hide movements, events, and assets tabs for transactions-only tenants
    if (!isTransactionsOnly) {
      tabItems.push({
        activeRouteIds: [`/app/locations/${locationIdNumber}/movements`],
        label: t('movements'),
        url: `/app/locations/${locationIdNumber}/movements`,
      });
      tabItems.push({
        activeRouteIds: [`/app/locations/${locationIdNumber}/events`],
        label: t('events'),
        url: `/app/locations/${locationIdNumber}/events`,
      });
    }
    tabItems.push({
      activeRouteIds: [`/app/locations/${locationIdNumber}/balance`],
      label: t('balances'),
      url: `/app/locations/${locationIdNumber}/balance`,
    });
    if (!isTransactionsOnly) {
      tabItems.push({
        activeRouteIds: [`/app/locations/${locationIdNumber}/assets`],
        label: t('assets'),
        url: `/app/locations/${locationIdNumber}/assets`,
      });
    }
    // Only show start balance tab for non-system locations with beta access
    if (hasBetaAccess && !location?.dto.isSystem) {
      tabItems.push({
        activeRouteIds: [`/app/locations/${locationIdNumber}/start-balance`],
        label: t('start_balance'),
        url: `/app/locations/${locationIdNumber}/start-balance`,
      });
    }
  }

  return (
    <LayoutActionsProvider>
      <TabbedLayout
        topBar={
          <TabbedLayoutTopBar>
            <BackButton pathname={backButtonPath} />
            <TabbedLayoutTopBarHeader icon={null}>
              {isCreate ? t('new_location') : location?.dto.name ? `${t('location')} - ${location.dto.name}` : <Skeleton width={200} />}
            </TabbedLayoutTopBarHeader>

            <TabbedLayoutTopBarVerticalSeperator />
            <NavigationTabs tabItems={tabItems} />
          </TabbedLayoutTopBar>
        }
      >
        <TabbedPageLayout>
          <TabbedPageLayoutBody>
            <div className="flex h-full flex-grow flex-col ">
              <Outlet />
            </div>
          </TabbedPageLayoutBody>
        </TabbedPageLayout>
      </TabbedLayout>
    </LayoutActionsProvider>
  );
};
